/**
 * Clipboard Image Upload Plugin for CKEditor 5
 * 
 * This plugin handles pasted images from the clipboard by intercepting
 * the paste event at the DOM level and manually triggering the upload.
 * This is necessary because CKEditor 5's default clipboard handling doesn't
 * always trigger uploads for pasted images.
 */

import { Plugin } from 'ckeditor5';

export default class ClipboardImageUpload extends Plugin {
	static get pluginName() {
		return 'ClipboardImageUpload';
	}

	static get requires() {
		return ['ImageUpload', 'FileRepository'];
	}

	init() {
		const editor = this.editor;
		
		// Listen to the native paste event on the editing view
		const view = editor.editing.view;
		const viewDocument = view.document;

		// Use high priority to handle before other paste handlers
		this.listenTo(viewDocument, 'paste', (evt, data) => {
			const dataTransfer = data.dataTransfer;
			
			// Check if there are files in the clipboard
			if (!dataTransfer || !dataTransfer.files || dataTransfer.files.length === 0) {
				return;
			}

			console.log('ClipboardImageUpload: Detected paste with files', {
				fileCount: dataTransfer.files.length
			});

			// Check if any of the files are images
			const imageFiles = Array.from(dataTransfer.files).filter(file => {
				return file && file.type && file.type.startsWith('image/');
			});

			if (imageFiles.length === 0) {
				console.log('ClipboardImageUpload: No image files found in paste');
				return;
			}

			console.log('ClipboardImageUpload: Found image files in paste', {
				count: imageFiles.length,
				types: imageFiles.map(f => f.type)
			});

			// Prevent default paste behavior for images
			evt.stop();
			data.preventDefault();

			// Upload each image file
			const fileRepository = editor.plugins.get('FileRepository');
			
			imageFiles.forEach(file => {
				console.log('ClipboardImageUpload: Creating loader for pasted image', {
					fileName: file.name || 'pasted-image.png',
					fileType: file.type,
					fileSize: file.size
				});

				// Create a file loader
				const loader = fileRepository.createLoader(file);

				if (loader) {
					// Insert the image into the editor
					loader.upload().then(response => {
						console.log('ClipboardImageUpload: Image uploaded successfully via paste', response);
						
						// Insert the image at the current cursor position
						editor.model.change(writer => {
							const imageElement = writer.createElement('imageBlock', {
								src: response.default
							});

							const insertPosition = editor.model.document.selection.getFirstPosition();
							editor.model.insertContent(imageElement, insertPosition);
						});
					}).catch(error => {
						console.error('ClipboardImageUpload: Failed to upload pasted image', error);
					});
				}
			});
		}, { priority: 'high' });
	}
}

